import { useState, useEffect } from 'react';
import { socialAPI, inventoryAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Share2, Facebook, Instagram, Linkedin, Sparkles, Send, Settings2, Megaphone, CheckCircle, XCircle, Loader2 } from 'lucide-react';

export default function SocialMedia() {
  const [tab, setTab] = useState('post');
  const [inventory, setInventory] = useState([]);
  const [selectedItem, setSelectedItem] = useState('');
  const [posts, setPosts] = useState({ facebook: '', instagram: '', linkedin: '' });
  const [platforms, setPlatforms] = useState(['facebook', 'instagram', 'linkedin']);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [history, setHistory] = useState([]);
  // Campaign state
  const [campaignName, setCampaignName] = useState('');
  const [campaignBudget, setCampaignBudget] = useState('10');
  const [campaignItem, setCampaignItem] = useState('');
  const [campaignText, setCampaignText] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  // Settings state
  const [settings, setSettings] = useState({
    fb_page_id: '', fb_page_access_token: '', ig_business_account_id: '',
    linkedin_company_id: '', linkedin_access_token: '', fb_ad_account_id: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    inventoryAPI.list({ limit: 200 }).then(r => setInventory(r.data.items || [])).catch(() => {});
    socialAPI.getPosts().then(r => setHistory(r.data || [])).catch(() => {});
    socialAPI.getCampaigns().then(r => setCampaigns(r.data || [])).catch(() => {});
    socialAPI.getSettings().then(r => setSettings(s => ({ ...s, ...r.data }))).catch(() => {});
  }, []);

  const togglePlatform = (p) => setPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);

  const handleGenerate = async () => {
    if (!selectedItem) return toast.error('Select an inventory item first');
    setGenerating(true);
    try {
      const res = await socialAPI.generatePost({ inventory_id: selectedItem, platforms });
      setPosts(res.data.posts);
      toast.success('AI posts generated!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate'); }
    setGenerating(false);
  };

  const handlePublish = async () => {
    if (!selectedItem || platforms.length === 0) return toast.error('Select item and platforms');
    setPublishing(true);
    try {
      const res = await socialAPI.publish({ inventory_id: selectedItem, platforms, posts });
      const r = res.data.results;
      Object.keys(r).forEach(p => {
        if (r[p].success) toast.success(`Posted to ${p}!`);
        else toast.error(`${p}: ${r[p].error}`);
      });
      socialAPI.getPosts().then(r => setHistory(r.data || [])).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to publish'); }
    setPublishing(false);
  };

  const handleCreateCampaign = async () => {
    if (!campaignName) return toast.error('Enter campaign name');
    setCreatingCampaign(true);
    try {
      const res = await socialAPI.createCampaign({
        name: campaignName, daily_budget: parseInt(campaignBudget) * 100,
        inventory_id: campaignItem || undefined, ad_text: campaignText,
        targeting_countries: ['CA'], age_min: 25, age_max: 65, status: 'PAUSED'
      });
      if (res.data.error) toast.error(res.data.error);
      else toast.success(`Campaign created! ID: ${res.data.campaign_id}`);
      socialAPI.getCampaigns().then(r => setCampaigns(r.data || [])).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setCreatingCampaign(false);
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await socialAPI.updateSettings(settings);
      toast.success('Settings saved!');
    } catch (e) { toast.error('Failed to save'); }
    setSavingSettings(false);
  };

  const PlatformIcon = ({ platform }) => {
    if (platform === 'facebook') return <Facebook className="w-4 h-4" />;
    if (platform === 'instagram') return <Instagram className="w-4 h-4" />;
    if (platform === 'linkedin') return <Linkedin className="w-4 h-4" />;
    return null;
  };

  const itemOptions = inventory.map(i => ({ value: i.id, label: i.title || `${i.year} ${i.make} ${i.model}` }));

  return (
    <div data-testid="social-media-page">
      <div className="flex items-center gap-2 mb-6">
        <Share2 className="w-5 h-5 text-[#E31937]" />
        <h1 className="text-xl font-bold text-gray-900">Social Media</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="post" data-testid="tab-post"><Share2 className="w-4 h-4 mr-1.5" />Post to Social</TabsTrigger>
          <TabsTrigger value="ads" data-testid="tab-ads"><Megaphone className="w-4 h-4 mr-1.5" />Facebook Ads</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings"><Settings2 className="w-4 h-4 mr-1.5" />API Settings</TabsTrigger>
        </TabsList>

        {/* ── Post to Social ── */}
        <TabsContent value="post">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-base">Select Equipment & Generate</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Select value={selectedItem} onValueChange={setSelectedItem}>
                  <SelectTrigger data-testid="select-inventory"><SelectValue placeholder="Choose inventory item..." /></SelectTrigger>
                  <SelectContent>{itemOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
                <div className="flex gap-2">
                  {['facebook', 'instagram', 'linkedin'].map(p => (
                    <Button key={p} size="sm" variant={platforms.includes(p) ? 'default' : 'outline'}
                      onClick={() => togglePlatform(p)} data-testid={`toggle-${p}`}
                      className={platforms.includes(p) ? 'bg-[#E31937] hover:bg-[#c5152f]' : ''}>
                      <PlatformIcon platform={p} /><span className="ml-1.5 capitalize">{p}</span>
                    </Button>
                  ))}
                </div>
                <Button onClick={handleGenerate} disabled={generating} className="w-full bg-[#0A0A0A] hover:bg-[#222]" data-testid="generate-posts-btn">
                  {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  {generating ? 'Generating with AI...' : 'Generate AI Posts'}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Preview & Edit</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {platforms.includes('facebook') && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 flex items-center gap-1 mb-1"><Facebook className="w-3.5 h-3.5 text-blue-600" />Facebook</label>
                    <Textarea value={posts.facebook} onChange={e => setPosts(p => ({ ...p, facebook: e.target.value }))} rows={4} data-testid="post-facebook" />
                  </div>
                )}
                {platforms.includes('instagram') && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 flex items-center gap-1 mb-1"><Instagram className="w-3.5 h-3.5 text-pink-500" />Instagram</label>
                    <Textarea value={posts.instagram} onChange={e => setPosts(p => ({ ...p, instagram: e.target.value }))} rows={4} data-testid="post-instagram" />
                  </div>
                )}
                {platforms.includes('linkedin') && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 flex items-center gap-1 mb-1"><Linkedin className="w-3.5 h-3.5 text-blue-700" />LinkedIn</label>
                    <Textarea value={posts.linkedin} onChange={e => setPosts(p => ({ ...p, linkedin: e.target.value }))} rows={4} data-testid="post-linkedin" />
                  </div>
                )}
                <Button onClick={handlePublish} disabled={publishing || !posts.facebook && !posts.instagram && !posts.linkedin}
                  className="w-full bg-[#E31937] hover:bg-[#c5152f]" data-testid="publish-btn">
                  {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  {publishing ? 'Publishing...' : 'Publish to Selected Platforms'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── Facebook Ads ── */}
        <TabsContent value="ads">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-base">Create Ad Campaign</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Input placeholder="Campaign Name" value={campaignName} onChange={e => setCampaignName(e.target.value)} data-testid="campaign-name" />
                <Select value={campaignItem} onValueChange={setCampaignItem}>
                  <SelectTrigger data-testid="campaign-inventory"><SelectValue placeholder="Select equipment (optional)..." /></SelectTrigger>
                  <SelectContent>{itemOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Daily Budget (CAD)</label>
                  <Input type="number" value={campaignBudget} onChange={e => setCampaignBudget(e.target.value)} data-testid="campaign-budget" />
                </div>
                <Textarea placeholder="Ad text / message" value={campaignText} onChange={e => setCampaignText(e.target.value)} rows={3} data-testid="campaign-text" />
                <p className="text-xs text-gray-400">Campaign will be created as PAUSED. Activate from Facebook Ads Manager.</p>
                <Button onClick={handleCreateCampaign} disabled={creatingCampaign} className="w-full bg-[#0A0A0A] hover:bg-[#222]" data-testid="create-campaign-btn">
                  {creatingCampaign ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Megaphone className="w-4 h-4 mr-2" />}
                  {creatingCampaign ? 'Creating...' : 'Create Campaign (Paused)'}
                </Button>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Campaign History</CardTitle></CardHeader>
              <CardContent>
                {campaigns.length === 0 ? <p className="text-sm text-gray-400">No campaigns yet</p> : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {campaigns.map(c => (
                      <div key={c.id} className="p-3 border rounded-lg text-sm">
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-gray-500 mt-1">{new Date(c.created_at).toLocaleDateString()}</div>
                        {c.results?.campaign_id && <Badge variant="outline" className="mt-1 text-green-600">ID: {c.results.campaign_id}</Badge>}
                        {c.results?.error && <Badge variant="destructive" className="mt-1">{c.results.error.slice(0, 60)}</Badge>}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── History ── */}
        <TabsContent value="history">
          <Card>
            <CardHeader><CardTitle className="text-base">Post History</CardTitle></CardHeader>
            <CardContent>
              {history.length === 0 ? <p className="text-sm text-gray-400">No posts yet</p> : (
                <div className="space-y-3">
                  {history.map(h => (
                    <div key={h.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">{new Date(h.created_at).toLocaleString()}</span>
                        <div className="flex gap-1.5">
                          {h.platforms?.map(p => (
                            <Badge key={p} variant={h.results?.[p]?.success ? 'default' : 'destructive'} className="text-xs flex items-center gap-1">
                              <PlatformIcon platform={p} />
                              {h.results?.[p]?.success ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      {h.posts?.facebook && <p className="text-xs text-gray-500 line-clamp-2">{h.posts.facebook.slice(0, 120)}...</p>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── API Settings ── */}
        <TabsContent value="settings">
          <Card>
            <CardHeader><CardTitle className="text-base">Social Media API Configuration</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3 p-4 border rounded-lg">
                  <h3 className="text-sm font-semibold flex items-center gap-2"><Facebook className="w-4 h-4 text-blue-600" />Facebook</h3>
                  <div><label className="text-xs text-gray-500">Page ID</label><Input value={settings.fb_page_id} onChange={e => setSettings(s => ({ ...s, fb_page_id: e.target.value }))} placeholder="123456789" data-testid="setting-fb-page-id" /></div>
                  <div><label className="text-xs text-gray-500">Page Access Token</label><Input type="password" value={settings.fb_page_access_token} onChange={e => setSettings(s => ({ ...s, fb_page_access_token: e.target.value }))} placeholder="EAA..." data-testid="setting-fb-token" /></div>
                  <div><label className="text-xs text-gray-500">Ad Account ID</label><Input value={settings.fb_ad_account_id} onChange={e => setSettings(s => ({ ...s, fb_ad_account_id: e.target.value }))} placeholder="123456789" data-testid="setting-fb-ad-account" /></div>
                </div>
                <div className="space-y-3 p-4 border rounded-lg">
                  <h3 className="text-sm font-semibold flex items-center gap-2"><Instagram className="w-4 h-4 text-pink-500" />Instagram</h3>
                  <div><label className="text-xs text-gray-500">Business Account ID</label><Input value={settings.ig_business_account_id} onChange={e => setSettings(s => ({ ...s, ig_business_account_id: e.target.value }))} placeholder="17841..." data-testid="setting-ig-id" /></div>
                  <p className="text-xs text-gray-400">Uses the same access token as Facebook</p>
                </div>
                <div className="space-y-3 p-4 border rounded-lg md:col-span-2">
                  <h3 className="text-sm font-semibold flex items-center gap-2"><Linkedin className="w-4 h-4 text-blue-700" />LinkedIn</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="text-xs text-gray-500">Company ID</label><Input value={settings.linkedin_company_id} onChange={e => setSettings(s => ({ ...s, linkedin_company_id: e.target.value }))} placeholder="5515715" data-testid="setting-li-company" /></div>
                    <div><label className="text-xs text-gray-500">Access Token</label><Input type="password" value={settings.linkedin_access_token} onChange={e => setSettings(s => ({ ...s, linkedin_access_token: e.target.value }))} placeholder="AQV..." data-testid="setting-li-token" /></div>
                  </div>
                </div>
              </div>
              <Button onClick={handleSaveSettings} disabled={savingSettings} className="bg-[#E31937] hover:bg-[#c5152f]" data-testid="save-social-settings">
                {savingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Settings2 className="w-4 h-4 mr-2" />}
                Save API Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
